import { z } from "zod";

/**
 * CT-RM-33: SCIM 2.0 (RFC 7643 / RFC 7644) user provisioning.
 *
 * Users map onto org users (userName = email); Groups map onto the org's
 * existing roles (one role per user, so joining a group sets the user's role).
 * The IdP authenticates with an org-scoped SCIM token — an API token carrying
 * only the "scim" scope, created in Settings › Security.
 */

/** mounted under the API prefix: <origin>/api/v1/scim/v2 */
export const SCIM_BASE_PATH = "/api/v1/scim/v2";
/** the API-token scope that marks a SCIM provisioning token; it grants nothing else */
export const SCIM_TOKEN_SCOPE = "scim";

export const SCIM_SCHEMAS = {
  user: "urn:ietf:params:scim:schemas:core:2.0:User",
  group: "urn:ietf:params:scim:schemas:core:2.0:Group",
  enterpriseUser: "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User",
  listResponse: "urn:ietf:params:scim:api:messages:2.0:ListResponse",
  patchOp: "urn:ietf:params:scim:api:messages:2.0:PatchOp",
  error: "urn:ietf:params:scim:api:messages:2.0:Error",
  serviceProviderConfig: "urn:ietf:params:scim:schemas:core:2.0:ServiceProviderConfig",
  resourceType: "urn:ietf:params:scim:schemas:core:2.0:ResourceType",
  schema: "urn:ietf:params:scim:schemas:core:2.0:Schema",
} as const;

/** the largest page a list request returns (ServiceProviderConfig.filter.maxResults) */
export const SCIM_MAX_RESULTS = 200;

/** IdPs send booleans as strings now and then (Entra: "False") */
export const zScimBoolean = z.union([z.boolean(), z.enum(["true", "false", "True", "False"]).transform((v) => v.toLowerCase() === "true")]);

const zScimName = z
  .object({
    formatted: z.string().max(200).optional(),
    givenName: z.string().max(100).optional(),
    familyName: z.string().max(100).optional(),
  })
  .passthrough();

const zScimEmail = z
  .object({
    value: z.string().trim().max(320),
    type: z.string().max(40).optional(),
    primary: zScimBoolean.optional(),
  })
  .passthrough();

/** POST /Users and PUT /Users/:id. Unknown attributes (enterprise extension, phoneNumbers…) are accepted and ignored. */
export const zScimUserInput = z
  .object({
    schemas: z.array(z.string()).optional(),
    userName: z.string().trim().min(1).max(320),
    externalId: z.string().trim().max(255).optional().nullable(),
    name: zScimName.optional(),
    displayName: z.string().trim().max(200).optional(),
    emails: z.array(zScimEmail).max(20).optional(),
    active: zScimBoolean.optional(),
  })
  .passthrough();
export type ScimUserInput = z.infer<typeof zScimUserInput>;

/** RFC 7644 §3.5.2. Entra capitalises op names ("Replace"); both spellings are accepted. */
export const zScimPatchOperation = z
  .object({
    op: z
      .string()
      .transform((v) => v.toLowerCase())
      .pipe(z.enum(["add", "replace", "remove"])),
    path: z.string().max(500).optional(),
    value: z.unknown().optional(),
  })
  .passthrough();
export type ScimPatchOperation = z.infer<typeof zScimPatchOperation>;

export const zScimPatch = z
  .object({
    schemas: z.array(z.string()).optional(),
    Operations: z.array(zScimPatchOperation).min(1).max(100),
  })
  .passthrough();
export type ScimPatch = z.infer<typeof zScimPatch>;

const zScimMemberRef = z.object({ value: z.string().min(1), display: z.string().optional() }).passthrough();

/** PUT /Groups/:id — only membership can change; displayName must stay the role's name */
export const zScimGroupInput = z
  .object({
    schemas: z.array(z.string()).optional(),
    displayName: z.string().trim().min(1).max(200),
    externalId: z.string().max(255).optional().nullable(),
    members: z.array(zScimMemberRef).max(5000).optional(),
  })
  .passthrough();
export type ScimGroupInput = z.infer<typeof zScimGroupInput>;

/** GET /Users and GET /Groups (RFC 7644 §3.4.2) */
export const zScimListQuery = z
  .object({
    filter: z.string().max(500).optional(),
    startIndex: z.coerce.number().int().catch(1).default(1),
    count: z.coerce.number().int().catch(100).default(100),
    attributes: z.string().max(500).optional(),
    excludedAttributes: z.string().max(500).optional(),
  })
  .passthrough();
export type ScimListQuery = z.infer<typeof zScimListQuery>;

// ---- Settings › Security (session-only admin endpoints) ----

export const zCreateScimToken = z.object({
  name: z.string().trim().min(1).max(100),
});
export type CreateScimToken = z.infer<typeof zCreateScimToken>;

export const zUpdateScimSettings = z.object({
  /** off (default): SCIM-created users are active immediately and get no invitation */
  sendInvites: z.boolean(),
});
export type UpdateScimSettings = z.infer<typeof zUpdateScimSettings>;

export interface ScimTokenSummary {
  id: string;
  name: string;
  prefix: string;
  createdByName: string | null;
  lastUsedAt: string | null;
  lastUsedIp: string | null;
  createdAt: string;
}

export interface ScimSettings {
  /** path of the SCIM base URL; the app prefixes its own origin */
  basePath: string;
  sendInvites: boolean;
  tokens: ScimTokenSummary[];
  /** users currently provisioned by the IdP */
  managedUsers: number;
}

export interface CreatedScimToken {
  id: string;
  /** raw token; returned exactly once */
  token: string;
}
